import React, { useCallback, useEffect, useRef, useState } from "react";
import { Suspense, lazy } from "react";

// import { useNavigate } from "react-router-dom";

import "./App.css";

// import { initializeApp } from "firebase/app";
// import { getAuth } from "firebase/auth";
// import { GoogleAuthProvider, EmailAuthProvider } from "firebase/auth";

import Navigation from "./components/Navigation";
import CardList from "./components/CardList";
import Header from "./components/Header";
import { ContextMenu } from "./components/ContextMenu";
import SelectedTag from "./components/SelectedTag";
import VibesView from "./components/VibesView";

const NewsRoute = lazy(() => import("./apps/News"));

const IntegrationRoute = lazy(() => import("./routes/Integrations"));
const TagRoute = lazy(() => import("./routes/Tags"));
const CreatorsRoute = lazy(() => import("./routes/Creators"));
const CreatorRoute = lazy(() => import("./routes/Creator"));
const EntryRoute = lazy(() => import("./routes/Entry"));
const HaikuRoute = lazy(() => import("./apps/Haiku"));
const EarthyTreesRoute = lazy(() => import("./apps/EarthyTrees"));
const EarthyMawRoute = lazy(() => import("./apps/EarthyMaw"));
const MacroeconomicsRoute = lazy(() => import("./apps/Macroeconomics"));
const AuditRoute = lazy(() => import("./routes/Audit"));
const TimelineRoute = lazy(() => import("./apps/Timeline"));
const HistoricNewsRoute = lazy(() => import("./apps/HistoricNews"));
const HistoricNewsSubmitRoute = lazy(() => import("./apps/HistoricNews/Submit"));
const HistoricNewsArticlesRoute = lazy(() => import("./apps/HistoricNews/Articles"));
const HistoricNewsShowRoute = lazy(() => import("./apps/HistoricNews/Show"));
const HistoricNewsCorrectionsRoute = lazy(() => import("./apps/HistoricNews/Corrections"));
const HistoricNewsArticleDebugRoute = lazy(() => import("./apps/HistoricNews/ArticleDebug"));
const TrainingRoute = lazy(() => import("./apps/Training"));
const RadioRoute = lazy(() => import("./apps/Radio"));
const ClassicalRoute = lazy(() => import("./apps/Classical"));

const { useLocation, useParams } = require("react-router-dom");

type RouteNames = "news" | "timeline" | "modquiz" | "entries" | "entry" | "haiku" | "integrations" | "integration" | "tags" | "creator" | "creators" | "vibes" | "earthytrees" | "earthymaw" | "macroeconomics" | "audit" | "historic_news" | "historic_news_submit" | "historic_news_articles" | "historic_news_article_show" | "historic_news_article_debug" | "historic_news_corrections" | "training" | "radio" | "classical";


// This configures the list of routes that should be displayed in full screen
// Others will be in a smaller container
const FULL_SCREEN_ROUTES = ["news", "earthytrees", "earthymaw", "vibes"];
const SELF_NAVIGATING_ROUTES = [
  "vibes",
  "news",
  "historic_news",
  "historic_news_submit",
  "historic_news_articles",
  "historic_news_article_show",
  "historic_news_article_debug",
  "historic_news_corrections",
  "radio",
  "classical",
];
const CONTAINED_ROUTES_CLASS = "max-w-2xl";

const route_screen_size = (route: string) => {
  if (route === "vibes") {
    return "max-w-screen-lg"
  }

  if (FULL_SCREEN_ROUTES.includes("earthytrees")) {
    return  ""
  }

  return "max-w-screen-sm"
}

function App({
  route,
}: {
  route: RouteNames
}) {

  let params = useParams();

  const [tag, setTag]: [string, Function] = useState(
    params["category"] || "default"
  );

  const [creator, setCreator]: [string, Function] = useState(
    params["creator"] || "default"
  );

  const [loading, setLoading]: [loading: boolean, setLoading: Function] =
    useState(false);

  const [aboutModalOpen, setAboutModalOpen] = useState(false);

  const location = useLocation();

  useEffect(() => {
    if (params["category"]) {
      setTag(params["category"]);
    } else {
      setTag("default");
    }

    if (params["creator"]) {
      setCreator(params["creator"]);
    } else {
      setCreator("default");
    }
  }, [location]);

  const renderSwitch = (route: RouteNames) => {
    switch (route) {
      case "entries":
        return (
          <CardList
            tag={tag}
            loading={loading}
            setCategory={setTag}
            setLoading={setLoading}
            feed={params["feed"]}
          />
        );
      case "vibes":
        return <VibesView />;
      case "earthytrees":
        return (
          <Suspense fallback={<>Route Loading...</>}>
            <EarthyTreesRoute />
          </Suspense>
        );
      case "earthymaw":
        return (
          <Suspense fallback={<>Route Loading...</>}>
            <EarthyMawRoute />
          </Suspense>
        );
      case "integrations":
        return (
          <Suspense fallback={<>Route Loading...</>}>
            <IntegrationRoute />
          </Suspense>
        );
      case "tags":
        return (
          <Suspense fallback={<>Route Loading...</>}>
            <TagRoute />;
          </Suspense>
        );
      case "creators":
        return (
          <Suspense fallback={<>Route Loading...</>}>
            <CreatorsRoute />
          </Suspense>
        );
      case "entry":
        return (
          <Suspense fallback={<>Route Loading...</>}>
            <EntryRoute />
          </Suspense>
        );
      case "haiku":
        return (
          <Suspense fallback={<>Route Loading...</>}>
            <div className="max-w-2xl mx-auto">
              <div className="flex">
                <HaikuRoute />
              </div>
            </div>
          </Suspense>
        );
      case "creator":
        return (
          <Suspense fallback={<>Route Loading...</>}>
            <CreatorRoute>
              <CardList
                creator={creator}
                loading={loading}
                setCategory={setTag}
                setLoading={setLoading}
                tag={tag}
                feed={params["feed"]}
              />
            </CreatorRoute>
          </Suspense>
        );
      case "macroeconomics":
        return (
          <Suspense fallback={<>Route Loading...</>}>
            <MacroeconomicsRoute />
          </Suspense>
        );
      case "timeline":
        return (
          <Suspense fallback={<>Route Loading...</>}>
            <TimelineRoute />
          </Suspense>
        );
      case "audit":
        return (
          <Suspense fallback={<>Route Loading...</>}>
            <AuditRoute />
          </Suspense>
        );
      case "news":
        return <Suspense fallback={<>Preparing the news…</>}><NewsRoute /></Suspense>;
      case "historic_news":
        return (
          <Suspense fallback={<>Route Loading...</>}>
            <HistoricNewsRoute />
          </Suspense>
        );
      case "historic_news_submit":
        return (
          <Suspense fallback={<>Route Loading...</>}>
            <HistoricNewsSubmitRoute />
          </Suspense>
        );
      case "historic_news_articles":
        return (
          <Suspense fallback={<>Route Loading...</>}>
            <HistoricNewsArticlesRoute />
          </Suspense>
        );
      case "historic_news_article_show":
        return (
          <Suspense fallback={<>Route Loading...</>}>
            <HistoricNewsShowRoute />
          </Suspense>
        );
      case "historic_news_article_debug":
        return (
          <Suspense fallback={<>Route Loading...</>}>
            <HistoricNewsArticleDebugRoute />
          </Suspense>
        );
      case "historic_news_corrections":
        return (
          <Suspense fallback={<>Route Loading...</>}>
            <HistoricNewsCorrectionsRoute />
          </Suspense>
        );
      case "training":
        return (
          <Suspense fallback={<>Route Loading...</>}>
            <TrainingRoute />
          </Suspense>
        );
      case "radio":
        return (
          <Suspense fallback={<>Tuning radio…</>}>
            <RadioRoute />
          </Suspense>
        );
      case "classical":
        return (
          <Suspense fallback={<>Preparing the listening room…</>}>
            <ClassicalRoute />
          </Suspense>
        );
    }
  }

  const handleShow = () => {
    setAboutModalOpen(!aboutModalOpen);
  }

  return (
    <div className="min-h-[calc(100dvh-2.5rem)] bg-stone-100 text-zinc-900 dark:bg-zinc-900 dark:text-stone-100">
      <SelectedTag tag={tag} />
      {!SELF_NAVIGATING_ROUTES.includes(route) && <Navigation />}
      <div>
        <ContextMenu />
      </div>
      <div
        className={`flex justify-between mx-auto ${route_screen_size(route)}`}
      >
        <div
          className={`${route_screen_size(route)} mx-auto w-full format format-sm sm:format-base lg:format-lg format-blue dark:format-invert `}
        >
          {route === "entries" ? (
            <div className="max-w-2xl mx-auto">
              <div className="flex">
                <div
                  className="scroll-smooth max-md:h-screen max-md:overflow-y-scroll"
                  // removedClassNames="max-md:snap-y max-md:snap-mandatory">
                >
                  <Header
                    handleShow={handleShow}
                    feedName={params["feed"]}
                    loading={loading}
                    aboutModalOpen={aboutModalOpen}
                  />
                  {renderSwitch(route)}
                </div>
              </div>
            </div>
          ) : (
            <header className="max-md:snap-always max-md:snap-start max-md:h-screen">
              {renderSwitch(route)}
            </header>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
